import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import "./AppTimeOffRequest.css";
import { PaidTimeOffPolicy, TimeOffRequest, TimeOffRequestStatus } from "winslow-core";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";
import DatePicker from "react-datepicker";
import { useNavigate, useParams } from "react-router-dom";
import { apiCreateTimeOffRequest, apiGetTimeOffRequest } from "../../../services/TimeOffRequestService";
import { apiGetPaidTimeOffPoliciesForWorkspace } from "../../../services/PaidTimeOffPolicyService";
import { getPTODataForEmployeeRecord, PTOData } from "./ptoUtils";

type AppTimeOffRequestProps = {
}

export const AppTimeOffRequest = ({ }: AppTimeOffRequestProps) => {

    const navigate = useNavigate();
    const params = useParams();

    const storeUser = useSelector(selectUser);


    const [isEditing, setIsEditing] = useState(false);
    const [timeOffRequest, setTimeOffRequest] = useState<TimeOffRequest | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [policies, setPolicies] = useState<PaidTimeOffPolicy[]>([]);
    const [timeRequested, setTimeRequested] = useState<number>(0);
    const [ptoData, setPTOData] = useState<PTOData | null>(null);

    const [requestData, setRequestData] = useState({
        policyId: "",
        startDate: new Date().toString(),
        endDate: new Date().toString(),
        notes: ""
    });

    useEffect(() => {

        if (!storeUser?.employeeRecordId) {
            toast("You are not an employee", toastFail);
            return;
        }

        if (storeUser?.currentWorkspaceId) {
            (async () => {
                const thePolicies = await apiGetPaidTimeOffPoliciesForWorkspace(storeUser.currentWorkspaceId || "");

                if (!thePolicies) {
                    toast("No PTO Policies found for this workspace", toastFail);
                    return;
                }
              
                setPolicies(thePolicies);
                if (thePolicies.length > 0) {
                    setRequestData({...requestData, policyId: thePolicies[0].id });
                }
            
                const thePTOData = await getPTODataForEmployeeRecord(
                    storeUser?.employeeRecordId || "",
                    thePolicies[0].id,
                    new Date()
                );

                setPTOData(thePTOData);
            })();
        }
    }, [storeUser]);

    useEffect(() => {
        if (params?.timeOffRequestId) {
            (async () => {
                const tor = await apiGetTimeOffRequest(params.timeOffRequestId!);
                if (!tor) {
                    toast("Failed to fetch time off request", toastFail);
                    return;
                }

                console.log("[AppTimeOffRequest] tor: ", tor);
                setTimeOffRequest(tor);
                setRequestData({
                    ...requestData,
                    policyId: tor.paidTimeOffPolicyId,
                    startDate: tor.startDate.toString(),
                    endDate: tor.endDate.toString(),
                    notes: tor.notes || ""
                });
            })();            
        }
    }, [params]);

    useEffect(() => {
        (async() => {
            if (requestData.policyId && storeUser?.employeeRecordId) {
                const ptoData = await getPTODataForEmployeeRecord(
                    storeUser?.employeeRecordId,
                    requestData.policyId,
                    new Date(requestData.startDate)
                )

                if (!ptoData) {
                    toast("Failed to fetch pto data", toastFail);
                    return;
                }

                setPTOData(ptoData);
            }
        })();
    }, [requestData.policyId, requestData.startDate, requestData.endDate]);


    const isAfter = (d1: Date, d2: Date) => {
        const d1WithoutTime = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
        const d2WithoutTime = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
        return d1WithoutTime > d2WithoutTime;
    }; 

    const calculateTimeRequested = (start: string, end: string) => {
        if (!start || !end) return 0;

        console.log("[AppTimeOffRequest] calculateTimeRequested, start: ", start);
        console.log("[AppTimeOffRequest] calculateTimeRequested, end: ", end);

        if (isAfter(new Date(), new Date(start))) {
            toast("Start date cannot be in the past", toastFail);
            return;
        }

        if (isAfter(new Date(start), new Date(end))) {
            toast("Start date cannot be after end date", toastFail);
            return;
        }

        const startDate = new Date(start);
        const endDate = new Date(end);

        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        setTimeRequested(diffDays);
    };
    
    const handleChangePolicy = (policyId: string) => {
        setRequestData({
            ...requestData,
            policyId: policyId,
        });
    }

    const handleSubmit = async (e: React.FormEvent) => {
        if (!storeUser?.currentWorkspaceId || !storeUser?.employeeRecordId) {
            setError("User information is missing");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const request: Partial<TimeOffRequest> = {
            id: "",
            employeeRecordId: storeUser.employeeRecordId,
            workspaceId: storeUser.currentWorkspaceId,
            paidTimeOffPolicyId: requestData.policyId,
            startDate: new Date(requestData.startDate),
            endDate: new Date(requestData.endDate),
            status: TimeOffRequestStatus.PENDING,
            notes: requestData.notes,
        }

        
        const response = await apiCreateTimeOffRequest(request);

        setIsSubmitting(false);

        if (!response) {
            toast("Failed to create time off request", toastFail);
            return;
        }

        navigate("/app/time-off");   
    }

    const availableBalance = ptoData ? ptoData.accrualMap.get(requestData.policyId) || 0 : 0;

    console.log("[AppTimeOffRequest] requestData: ", requestData);
    console.log("[AppTimeOffRequest] ptoData: ", ptoData);
    console.log("[AppTimeOffRequest] ptoData.accrualMap: ", ptoData?.accrualMap);

    return (
        <div className="app-time-off-request">
            <h1>Request time off</h1>
            
            <div className="app-time-off-request-container">
                <div className="app-time-off-request-request">

                    <div className="app-time-off-form-section">
                        <label htmlFor="policy">Select policy</label>
                        <select
                            id="policy"
                            value={requestData.policyId}
                            onChange={(e) => {
                                handleChangePolicy(e.target.value);
                                // setRequestData({ ...requestData, policyId: e.target.value })
                            }}
                            required
                        >
                            {policies.map(policy => (
                                <option key={policy.id} value={policy.id}>
                                    {policy.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="app-time-off-date-section">
                        <div className="app-time-off-form-group">
                            <label htmlFor="startDate">Start date</label>
                            <DatePicker
                                selected={requestData.startDate ? new Date(requestData.startDate) : null} 
                                dateFormat={"MM/dd/yyyy"}
                                onChange={(date) => {
                                    console.log("DatePicker start date", date);
                                    const startDate = date ? date.toISOString() : "";
                                    const endDate = !date ? requestData.endDate :
                                        (date.getTime() > new Date(requestData.endDate).getTime()) ? date.toISOString() : requestData.endDate;

                                    setRequestData({
                                        ...requestData,
                                        startDate,
                                        endDate
                                    });

                                    calculateTimeRequested(startDate, endDate);
                                    
                                }}
                                className="date-picker-input"
                                wrapperClassName="app-date-picker-wrapper"
                            />
                            {/* <input
                                type="date"
                                id="startDate"
                                value={requestData.startDate}
                                onChange={(e) => handleDateChange('startDate', e.target.value)}
                                required
                            /> */}
                        </div>

                        <div className="app-time-off-form-group">
                            <label htmlFor="endDate">End date</label>
                            <DatePicker
                                selected={requestData.endDate ? new Date(requestData.endDate) : null} 
                                dateFormat={"MM/dd/yyyy"}
                                onChange={(date) => {
                                    console.log("DatePicker end date", date);
                                    const endDate = date ? date.toISOString() : "";
                                    const startDate = !date ? requestData.startDate :
                                        (date.getTime() < new Date(requestData.startDate).getTime()) ? date.toISOString() : requestData.startDate;

                                    setRequestData({
                                        ...requestData,
                                        startDate,
                                        endDate
                                    });

                                    calculateTimeRequested(startDate, endDate);
                                }}
                                className="date-picker-input"
                                wrapperClassName="app-date-picker-wrapper"
                            />
                            {/* <input
                                type="date"
                                id="endDate"
                                value={requestData.endDate}
                                onChange={(e) => handleDateChange('endDate', e.target.value)}
                                required
                            /> */}
                        </div>
                    </div>

                    <div className="app-time-off-form-section">
                        <label htmlFor="notes">Add comment <span className="app-time-off-optional">Optional</span></label>
                        <p className="app-time-off-note">Your manager will receive this message</p>
                        <textarea
                            id="notes"
                            value={requestData.notes}
                            onChange={(e) => setRequestData({ ...requestData, notes: e.target.value })}
                        />
                    </div>

                    {error && <div className="app-time-off-error-message">{error}</div>}
                </div>

                

                <div className="app-time-off-review-panel">
                    <h2>Review</h2>
                    <div className="app-time-off-balance-info">
                        <div className="app-time-off-balance-row">
                            <span>Available balance</span>
                            <span>{availableBalance} days</span>
                        </div>
                        <div className="app-time-off-balance-row">
                            <span>Time requested</span>
                            <span>{timeRequested} days</span>
                        </div>
                        <div className="app-time-off-balance-row">
                            <span>Remaining</span>
                            <span className={availableBalance - timeRequested < 0 ? 'app-time-off-negative' : ''}>
                                {availableBalance - timeRequested} days
                            </span>
                        </div>
                    </div>

                </div>
            </div>

            <div className="app-time-off-request-actions">
                <button 
                    type="submit" 
                    className="button-save"
                    disabled={
                        isSubmitting || 
                        timeRequested > availableBalance || 
                        !requestData.policyId || 
                        isAfter(new Date(), new Date(requestData.startDate)) ||
                        isAfter(new Date(requestData.startDate), new Date(requestData.endDate))
                    }
                    onClick={handleSubmit}
                >
                    Submit request
                </button>

                <button 
                    type="button" 
                    className="button-cancel"
                    onClick={() => navigate("/app/time-off")}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}; 